// errorReporting.js — envío best-effort de errores del cliente a
// /.netlify/functions/logClientError (ErrorBoundary + handlers globales).
//
// Con throttle y deduplicación en memoria para no inundar: como mucho
// MAX_PER_WINDOW envíos por ventana, y no se repite el mismo error (misma
// firma) dos veces en la misma ventana.
import { authService } from '../services/authService';
import { trackEvent } from '../lib/analytics';

const WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 8;

let windowStart = 0;
let sentInWindow = 0;
const recentSignatures = new Set();

function appVersion() {
  try {
    // CRA expone REACT_APP_* ; si no hay, cae a '' sin romper.
    return process.env.REACT_APP_COMMIT_REF || process.env.REACT_APP_VERSION || '';
  } catch { return ''; }
}

function throttled(signature) {
  const now = Date.now();
  if (now - windowStart > WINDOW_MS) {
    windowStart = now;
    sentInWindow = 0;
    recentSignatures.clear();
  }
  if (recentSignatures.has(signature)) return true;
  if (sentInWindow >= MAX_PER_WINDOW) return true;
  recentSignatures.add(signature);
  sentInWindow += 1;
  return false;
}

/**
 * Envía un error al backend. Nunca lanza; nunca bloquea.
 * @param {Error|string} error
 * @param {{ source?: string, componentStack?: string, context?: any }} [meta]
 */
export function reportClientError(error, meta = {}) {
  try {
    const message = (error && error.message) || String(error || '(sin mensaje)');
    const stack = (error && error.stack) || '';
    const signature = `${meta.source || 'manual'}::${message}`.slice(0, 300);
    if (throttled(signature)) return;

    const payload = {
      message,
      stack,
      componentStack: meta.componentStack || '',
      source: meta.source || 'manual',
      url: typeof window !== 'undefined' ? window.location.href : '',
      context: meta.context ?? '',
      appVersion: appVersion(),
    };

    const body = JSON.stringify(payload);
    const headers = { 'Content-Type': 'application/json', ...authService.authHeader() };

    // keepalive permite que el envío sobreviva a un unload/navegación.
    fetch('/.netlify/functions/logClientError', { method: 'POST', headers, body, keepalive: true })
      .catch(() => {});

    // También a PostHog (no solo al panel de errores propio) — así el error
    // queda enlazado a la grabación de sesión del piloto, que Firestore no
    // puede dar.
    trackEvent('error_app', { source: meta.source || 'manual', message: message.slice(0, 200) });
  } catch {
    /* el reporte de errores jamás debe provocar otro error */
  }
}

let installed = false;

/** Registra window.onerror y unhandledrejection una sola vez. */
export function installGlobalErrorHandlers() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e) => {
    // Errores de carga de recursos (img/script) llegan aquí sin e.error.
    if (!e || (!e.error && !e.message)) return;
    reportClientError(e.error || e.message, {
      source: 'window.onerror',
      context: e.filename ? `${e.filename}:${e.lineno || 0}:${e.colno || 0}` : '',
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e && e.reason;
    reportClientError(reason instanceof Error ? reason : new Error(String(reason)), {
      source: 'unhandledrejection',
    });
  });
}
